/**
 * Reads behind the app shell: sidebar spend meter, last-run box, the Opportunities badge,
 * the top-bar freshness pill, and the global pause banner. Keys start with ['seo', <table>],
 * so invalidating a table after a write refreshes the shell too.
 */
import { supabase } from '../supabase';
import { useSb } from '../query';
import type { Brand } from '../brand';
import type { SpendLayer } from '../../components/SpendMeter';

/** Opportunity statuses that still need someone to act (spec §8 life cycle). */
export const OPEN_STATUSES = ['open', 'assigned', 'in_progress'] as const;

/** Month-to-date spend per layer for a brand, with that brand's budgets. */
export function useSpend(brand: Brand | null) {
  const q = useSb(
    ['seo', 'v_spend_month', brand?.id],
    () => supabase.from('v_spend_month').select('layer, spent_usd').eq('brand_id', brand!.id),
    { enabled: !!brand },
  );
  const spent = (layer: 'seo' | 'ai') =>
    (q.data ?? []).filter((r) => r.layer === layer).reduce((sum, r) => sum + Number(r.spent_usd ?? 0), 0);
  const layers: SpendLayer[] | null = brand
    ? [
        { layer: 'seo', spent: spent('seo'), budget: Number(brand.budget_core_usd) },
        { layer: 'ai', spent: spent('ai'), budget: Number(brand.budget_ai_usd) },
      ]
    : null;
  return { ...q, layers };
}

/** The latest `jobs` row for a brand, any status. */
export function useLastRun(brandId: string | null) {
  return useSb(
    ['seo', 'jobs', 'last', brandId],
    async () => {
      const r = await supabase
        .from('jobs')
        .select('kind, status, created_at, finished_at, result, error')
        .eq('brand_id', brandId!)
        .order('created_at', { ascending: false })
        .limit(1);
      return { data: r.data?.[0] ?? null, error: r.error };
    },
    { enabled: !!brandId },
  );
}

/** When data was last collected: the newest finished `done` job, for one brand or all of them. */
export function useLastCollected(brandId: string | null) {
  return useSb(['seo', 'jobs', 'last-done', brandId ?? 'all'], async () => {
    let q = supabase.from('jobs').select('finished_at').eq('status', 'done').not('finished_at', 'is', null);
    if (brandId) q = q.eq('brand_id', brandId);
    const r = await q.order('finished_at', { ascending: false }).limit(1);
    return { data: r.data?.[0]?.finished_at ?? null, error: r.error };
  });
}

/** Open opportunities at attention level, for the sidebar badge. */
export function useAttentionCount(brandId: string | null) {
  return useSb(
    ['seo', 'opportunities', 'attention-count', brandId],
    async () => {
      const r = await supabase
        .from('opportunities')
        .select('id', { count: 'exact', head: true })
        .eq('brand_id', brandId!)
        .eq('level', 'attention')
        .in('status', [...OPEN_STATUSES]);
      return { data: r.count ?? 0, error: r.error };
    },
    { enabled: !!brandId },
  );
}

/** The `app_settings` singleton (global pause). */
export function useAppSettings() {
  return useSb(['seo', 'app_settings'], () => supabase.from('app_settings').select('paused, notes').maybeSingle());
}
